import { Box, Button, FormControl, Grid, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { EMonetaryAmountBy } from "../../../invoice/create/interfaces/invoice.interfaces";
import { colors, alphaVar } from "../../../ui/colors";
import { menuPropsModal } from "../../../../utils";
import type { Job } from "../../interfaces/job.interfaces";
import type { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";
import { useModals } from "src/context/ModalContext";
import { usePatchJobPricing } from "../hooks/usePatchJobPricing";
import { buildJobPricingPatchPayload } from "../utils/buildJobPricingPatchPayload";
import {
  extractJobPricingDefaults,
  isFlatFeePricingType,
  type JobPricingFormValues,
} from "../utils/extractJobPricingDefaults";
import {
  formatJobServiceCostCurrency,
  resolveJobServiceCost,
} from "../utils/resolveJobServiceCost";

const ADVANCED_PRICING_OPTIONS = [
  { value: EMonetaryAmountBy.MONTHLY, label: "Monthly" },
  { value: EMonetaryAmountBy.YEARLY, label: "Yearly" },
  { value: EMonetaryAmountBy.UNIQUE, label: "One Time" },
  { value: EMonetaryAmountBy.PER_ITEM, label: "Per Item" },
  { value: EMonetaryAmountBy.PER_SQUARE_FOOT, label: "Per Square Foot" },
  { value: EMonetaryAmountBy.PER_CAR_SPACE, label: "Per Car Space" },
  { value: EMonetaryAmountBy.PER_FLOOR, label: "Per Floor" },
];

const BASIC_MEASURE_OPTIONS = [
  { value: "sqft", label: "Square Feet" },
  { value: "sqm", label: "Square Meters" },
  { value: "minutes", label: "Minute(s)" },
  { value: "hours", label: "Hour(s)" },
  { value: "days", label: "Day(s)" },
  { value: "weeks", label: "Week(s)" },
  { value: "months", label: "Month(s)" },
  { value: EMonetaryAmountBy.PER_ITEM, label: "Per Item" },
  { value: EMonetaryAmountBy.UNIQUE, label: "One Time" },
];

type Props = {
  job: Job;
  scopeOfWork?: ScopeOfWork | null;
};

function computePreviewTotal(values: JobPricingFormValues, isAdvanced: boolean): number | null {
  const price = values.price === "" ? NaN : Number(values.price);
  if (!Number.isFinite(price) || price <= 0) return null;

  if (isAdvanced && isFlatFeePricingType(values.by)) {
    return price;
  }

  if (!isAdvanced && isFlatFeePricingType(values.by)) {
    return price;
  }

  const units = values.units === "" ? NaN : Number(values.units);
  if (Number.isFinite(units) && units > 0) {
    return price * units;
  }

  return price;
}

export function JobServiceCostEdit({ job, scopeOfWork }: Props) {
  const isAdvanced = job.type === "advanced";
  const { closeByKey } = useModals();
  const { mutate: patchPricing, isPending } = usePatchJobPricing();

  const [values, setValues] = useState<JobPricingFormValues>(() =>
    extractJobPricingDefaults(job, scopeOfWork),
  );

  const previewTotal = computePreviewTotal(values, isAdvanced);
  const currentCost = resolveJobServiceCost(job, scopeOfWork);
  const hasScopeOfWork = Boolean(job.scopeOfWorkUid);

  const handleChange = (field: keyof JobPricingFormValues, value: string) => {
    setValues((prev) => {
      if (field !== "by") {
        return { ...prev, [field]: value };
      }

      return {
        ...prev,
        by: value,
        units: isFlatFeePricingType(value) ? "" : prev.units,
      };
    });
  };

  const handleSave = () => {
    const priceNum = values.price === "" ? NaN : Number(values.price);
    if (!Number.isFinite(priceNum) || priceNum <= 0) return;

    const payload = buildJobPricingPatchPayload(job.type, values);

    patchPricing(
      { jobId: job.id, payload },
      { onSuccess: () => closeByKey(`Job-Edit-Pricing-${job.id}`) },
    );
  };

  const showUnits = !isFlatFeePricingType(values.by);

  if (!hasScopeOfWork) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          This job has no scope of work linked. Use Edit Job to configure pricing.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560, mx: "auto" }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: colors.gray[800] }}>
          {isAdvanced ? "Advanced job pricing" : "Basic job pricing"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>
          Update only the pricing for this job. The backend will apply the override to the scope of work.
        </Typography>
      </Stack>

      {currentCost ? (
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alphaVar(colors.gray[100], 0.6),
            border: `1px solid ${colors.gray[200]}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
            Current total
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 20, mt: 0.25 }}>
            {formatJobServiceCostCurrency(currentCost.amount, currentCost.currency)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
            Source: {currentCost.source}
          </Typography>
        </Box>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={showUnits ? 6 : 12}>
          <FormControl fullWidth size="small">
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: colors.gray[500],
                mb: 0.75,
              }}
            >
              {isAdvanced ? "Pricing type" : "Measure"}
            </Typography>
            <Select
              MenuProps={menuPropsModal}
              value={values.by}
              onChange={(e) => handleChange("by", e.target.value)}
              sx={{ borderRadius: 2, bgcolor: colors.gray[50] }}
            >
              {(isAdvanced ? ADVANCED_PRICING_OPTIONS : BASIC_MEASURE_OPTIONS).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={showUnits ? 6 : 12}>
          <FormControl fullWidth>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: colors.gray[500],
                mb: 0.75,
              }}
            >
              {isAdvanced || isFlatFeePricingType(values.by) ? "Amount (USD)" : "Unit price (USD)"}
            </Typography>
            <TextField
              type="number"
              size="small"
              value={values.price}
              onChange={(e) => handleChange("price", e.target.value)}
              placeholder="0.00"
              inputProps={{ min: 0, step: "0.01" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: colors.gray[50],
                },
              }}
            />
          </FormControl>
        </Grid>

        {showUnits ? (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: colors.gray[500],
                  mb: 0.75,
                }}
              >
                {isAdvanced ? "Units / quantity" : "Quantity"}
              </Typography>
              <TextField
                type="number"
                size="small"
                value={values.units}
                onChange={(e) => handleChange("units", e.target.value)}
                placeholder="0"
                inputProps={{ min: 0, step: "1" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: colors.gray[50],
                  },
                }}
              />
            </FormControl>
          </Grid>
        ) : null}
      </Grid>

      {previewTotal != null ? (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alphaVar(colors.orange.base, 0.07)} 0%, ${alphaVar(colors.orange.base, 0.03)} 100%)`,
            border: `1px solid ${alphaVar(colors.orange.base, 0.15)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: 13, color: colors.gray[600] }}>New estimated total</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: colors.orange.base }}>
            {formatJobServiceCostCurrency(previewTotal, values.currency)}
          </Typography>
        </Box>
      ) : null}

      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => closeByKey(`Job-Edit-Pricing-${job.id}`)}
          disabled={isPending}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending || values.price === "" || Number(values.price) <= 0}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {isPending ? "Saving…" : "Save pricing"}
        </Button>
      </Stack>
    </Box>
  );
}
